import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from "react";
import { request } from "@/services/request";
import { parseJSON, removeMultipleKeys } from "@/utils/function";
import { NavigateFunction } from "react-router-dom";
import { toast } from "sonner";

type UserDetails = {
  id: string;
  lastName: string;
  firstName: string;
  email: string;
  isVerified: boolean;
};

// @ts-ignore
// interface IAuthContext {
//   token: string | null;
//   details: UserDetails | null;
//   isAuthenticated: boolean;
//   handleLogin?: (args: Login) => Promise<void>;
//   handleLogout?: () => void;
//   setRegisterLoading: Dispatch<SetStateAction<boolean>>;
//   registerLoading: boolean;
// }

const TOKEN_KEY = "pluri_token";
const REFRESH_KEY = "pluri_refresh_token";
const USER_DETAIL_KEY = "user_detail";

const INITIAL_STATE: IAuthContext = {
  token: localStorage.getItem(TOKEN_KEY),
  refreshToken: localStorage.getItem(REFRESH_KEY),
  details: parseJSON<UserDetails>(localStorage.getItem(USER_DETAIL_KEY)),
  isAuthenticated: !!localStorage.getItem(TOKEN_KEY),
} as IAuthContext;

interface GameTimeStamp {
  start: number;
  end: number;
}

interface ActivityValue {
  lessonId: string;
  levelId: string;
  language: string;
  userId: string;
  accuracy: number;
  timeToComplete: string;
  summary: string[];
}

export interface Login {
  email: string;
  password: string;
}

export interface Register {
  age: string;
  email: string;
  lastName: string;
  firstName: string;
  password: string;
  guardian: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    Email: string;
  };
}

interface IAuthContext {
  token: string | null;
  refreshToken: string | null;
  details: UserDetails | null;
  isAuthenticated: boolean;
  handleLogin: (args: Login) => Promise<void>;
  handleLogout: () => void;
  gameTimeStamp: GameTimeStamp;
  updateGameTimeStamp: Dispatch<SetStateAction<GameTimeStamp>>;
  activityValue: ActivityValue;
  updateActivityValues: (activity: ActivityValue) => void;
  trialCount: number;
  updateTrialCount: Dispatch<SetStateAction<number>>;
  register: (args: Register, navigate: NavigateFunction) => Promise<void>;
  registerLoading: boolean;
  setRegisterLoading: Dispatch<SetStateAction<boolean>>;
}

export const AuthContext = createContext<IAuthContext | undefined>(undefined);

export function AuthContextProvider({ children }: { children: ReactNode }) {
  const [registerLoading, setRegisterLoading] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(localStorage.getItem(TOKEN_KEY));
  const [refreshToken, setRefreshToken] = useState<string | null>(localStorage.getItem(TOKEN_KEY));

  const [details, updateDetails] = useState<UserDetails | null>(INITIAL_STATE.details);
  const [gameTimeStamp, updateGameTimeStamp] = useState<GameTimeStamp>(INITIAL_STATE.gameTimeStamp);
  const [activityValue, updateActivityValues] = useState<ActivityValue>(
    INITIAL_STATE.activityValue
  );
  const [trialCount, updateTrialCount] = useState<number>(INITIAL_STATE.trialCount);

  const handleLogin = async ({ email, password }: Login) => {
    try {
      const { data } = await request.post(`${process.env.REACT_APP_API_URL}/auth/login`, {
        email,
        password,
      });
      const token = data.result.accessToken;
      const refreshKey = data.result.refreshToken;
      const detail = data.result.userDetails;
      setToken(token);
      updateDetails(detail);
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(REFRESH_KEY, refreshKey);
      localStorage.setItem(USER_DETAIL_KEY, JSON.stringify(detail));
    } catch (error: any) {
      console.error("Login failed:", error.response?.data?.message || error);
      throw error;
    }
  };

  const register = async (body: any, navigate: any) => {
    setRegisterLoading(true);
    const { data } = await request.post(`${process.env.REACT_APP_API_URL}/auth/register`, body);

    // @ts-ignore
    if (data?.result) {
      toast.success("Registration successful", {
        richColors: true,
      });
      setRegisterLoading(false);
      navigate("/welcome", {
        state: {
          // @ts-ignore
          token: data?.result?.accessToken,
          // @ts-ignore
          refreshToken: data?.result?.refreshToken,
          // @ts-ignore
          result: data?.result?.userDetails,
        },
      });
    } else {
      setRegisterLoading(false);
    }
  };

  const handleLogout = () => {
    setToken(null);
    updateDetails(null);
    removeMultipleKeys([TOKEN_KEY, REFRESH_KEY, USER_DETAIL_KEY, "toggle"]);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        refreshToken,
        details,
        isAuthenticated: !!token,
        handleLogin,
        handleLogout,
        register,
        setRegisterLoading,
        registerLoading,
        gameTimeStamp,
        updateGameTimeStamp,
        activityValue,
        updateActivityValues,
        trialCount,
        updateTrialCount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthContextProvider");
  }
  return context;
};
